import * as React from "react";

import {
  ChartTooltipBody,
  type ChartTooltipRow,
} from "@/components/ui/chart-tooltip-body";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * The standard chart tooltip for the whole app. Any chart with a hover
 * tooltip should render this rather than hand-rolling `TooltipContent` — it
 * bakes in the house layout: a centered header, one dot·label / value row per
 * series (values right-aligned + `tabular-nums`), and an optional bold total
 * separated by a divider.
 *
 * ```tsx
 * <ChartTooltip
 *   trigger={<rect x={x} y={y} width={w} height={h} fill="transparent" />}
 *   title="BP Energy · 12-month"
 *   rows={[
 *     { label: "Energy",   value: "$0.0490", dot: "var(--primary-fill)" },
 *     { label: "Capacity", value: "$0.0141", dot: "var(--warning)" },
 *   ]}
 *   total={{ label: "All-in", value: "$0.0631" }}
 * />
 * ```
 *
 * `trigger` is rendered via Radix `asChild`, so it must be a single element
 * that forwards a ref (an SVG `<rect>`, a `<button>`, etc.).
 *
 * **Canvas charts cannot use this** — they have no per-series DOM element to
 * trigger on, and their position comes from a crosshair callback rather than
 * Popper. They render `<ChartTooltipBody>` (the shared inside of this
 * component) in their own positioned container. Never hand-roll a third
 * layout.
 */
export type { ChartTooltipRow };

export function ChartTooltip({
  trigger,
  title,
  rows,
  total,
  className,
}: {
  trigger: React.ReactNode;
  /** Header line (e.g. `"BP Energy · 12-month"`). Optional. */
  title?: React.ReactNode;
  rows: ChartTooltipRow[];
  /** Optional bold summary row, divided from the series rows above. */
  total?: { label: string; value: string };
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent className={cn("min-w-[196px] p-0", className)}>
        <ChartTooltipBody title={title} rows={rows} total={total} />
      </TooltipContent>
    </Tooltip>
  );
}
